/**
 * Put states into one machine, as usages of their defs (decision 030, B and C).
 *
 * Opened two ways: a transition drawn between states that share no machine (B),
 * and "Group into machine" on a selection (C). Either way nothing moves and no
 * reference is rewritten: the machine gains a usage of each state's def, and the
 * view is pointed at the machine (`expose M::**`), which pairs it - so what the
 * diagram shows afterwards is the machine and what it holds.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { EntityRow, StateGroupResult } from '../../schema'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import { elementOption } from '../common/elements'

export default function GroupDialog({
  states,
  view,
  machines,
  preferred,
  why,
  onClose,
  onGrouped,
}: {
  /** The states to put in: defs, or usages of other machines. */
  states: { id: string; name: string }[]
  /** The view to point at the machine. */
  view: string
  /** The machines offered - state defs declared in no other state. */
  machines: EntityRow[]
  /** The machine offered first: the view's own, when it is paired. */
  preferred?: string
  /** Why the dialog opened, when it was a refused drawing. */
  why?: string
  onClose: () => void
  onGrouped: (result: StateGroupResult) => void
}) {
  const [mode, setMode] = useState<'existing' | 'new'>(preferred || machines.length ? 'existing' : 'new')
  const [machine, setMachine] = useState(preferred ?? '')
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  // Not a state being grouped: a machine cannot hold itself.
  const offered = machines.filter((row) => !states.some((state) => state.id === row.id))
  const ready = mode === 'existing' ? Boolean(machine) : Boolean(identifier || name)
  const names = states.map((state) => state.name).join(', ')

  const submit = () => {
    setBusy(true)
    setFailure('')
    api
      .groupStates({
        states: states.map((state) => state.id),
        view,
        ...(mode === 'existing'
          ? { machine }
          : { new_machine: { identifier, ...(name ? { name } : {}) } }),
      })
      .then(onGrouped)
      .catch((error: ApiError) => setFailure(error.message))
      .finally(() => setBusy(false))
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Put {names} into a machine</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {why && <Alert severity="info">{why}</Alert>}
          {failure && <Alert severity="error">{failure}</Alert>}
          <Typography variant="body2" color="text.secondary">
            The machine gains a usage of each one&apos;s def; nothing is moved or renamed. This view
            then shows that machine and the states in it.
          </Typography>
          <RadioGroup value={mode} onChange={(event) => setMode(event.target.value as 'existing' | 'new')}>
            <FormControlLabel value="existing" control={<Radio size="small" />} label="An existing machine"
              disabled={offered.length === 0} />
            <FormControlLabel value="new" control={<Radio size="small" />} label="A new machine" />
          </RadioGroup>
          {mode === 'existing' ? (
            <ElementPicker
              rows={offered}
              label="Machine"
              required
              value={machine || null}
              onChange={setMachine}
              describe={(row, value) => ({ ...elementOption(row, value), chosen: `${row.name || row.id} (${row.id})` })}
            />
          ) : (
            <>
              <TextField
                label="Machine ID"
                size="small"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                helperText="The new state def's short name, e.g. MD_POWER"
                slotProps={{ htmlInput: { style: { fontFamily: MONOSPACE } } }}
              />
              <TextField
                label="Machine name"
                size="small"
                value={name}
                onChange={(event) => setName(event.target.value)}
                helperText="Declared beside this view, in its package"
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!ready || busy}>
          {busy ? 'Putting in…' : 'Put in'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
